import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Modal,
  Paper,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Divider,
  Tooltip,
  Menu,
  MenuItem,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FolderIcon from '@mui/icons-material/Folder';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import FilterListIcon from '@mui/icons-material/FilterList';
import CheckIcon from '@mui/icons-material/Check';

const StudentQuizzes = () => {
  const theme = useTheme();
  const token = sessionStorage.getItem("student_token");
  const [folders, setFolders] = useState([]);
  const [studentName, setStudentName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [quizHistory, setQuizHistory] = useState([]);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewData, setReviewData] = useState(null);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [searchFilter, setSearchFilter] = useState("all");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    fetch(`${API_BASE_URL}/student/folders/`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load courses.");
        return res.json();
      })
      .then((data) => {
        setStudentName(data.student_name || "");
        const folderList = data.assigned_folders || [];
        const fetchQuizzes = folderList.map((folder) =>
          fetch(`${API_BASE_URL}/student/folder/${folder.id}/quizzes/`, {
            headers: { Authorization: `Bearer ${token}` },
          })
            .then((res) => {
              if (!res.ok) throw new Error(`Failed to load quizzes for courses ${folder.id}`);
              return res.json();
            })
            .then((quizData) => ({ ...folder, quizzes: quizData.quizzes }))
            .catch((err) => {
              console.error(err);
              return { ...folder, quizzes: [], error: err.message };
            })
        );
        Promise.all(fetchQuizzes).then((updatedFolders) => {
          setFolders(updatedFolders);
          setLoading(false);
        });
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to load courses.");
        setLoading(false);
      });
  }, [navigate, token]);

  const fetchQuizHistory = async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const res = await axios.get(`${API_BASE_URL}/quiz-history/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuizHistory(res.data.history || []);
      setHistoryModalOpen(true);
    } catch (err) {
      console.error("Error fetching quiz history:", err);
      setSnackbarMessage("Failed to load history.");
      setSnackbarOpen(true);
      setSnackbarSeverity("error");
    }
  };

  const fetchReviewQA = async (quizId, attemptId) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const res = await axios.get(`${API_BASE_URL}/quiz/${quizId}/review/${attemptId}/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviewData(res.data);
      setReviewModalOpen(true);
    } catch (err) {
      console.error("Error fetching review:", err);
      setSnackbarMessage("Failed to fetch review.");
      setSnackbarOpen(true);
      setSnackbarSeverity("error");
    }
  };

  const startQuiz = (quizId) => {
    navigate(`/quiz-instructions/${quizId}`);
  };

  if (loading)
    return (
      <Box
        sx={{
          textAlign: "center",
          mt: 10,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={60} thickness={4} sx={{ color: "#667eea" }} />
        <Typography
          variant="h6"
          mt={2}
          sx={{ color: theme.palette.text.secondary, fontWeight: "600" }}
        >
          Loading your quizzes...
        </Typography>
      </Box>
    );

  if (error)
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );

  const filteredFolders = folders.filter((folder) => {
    if (!searchQuery) return true;

    const folderNameMatch = folder.name.toLowerCase().includes(searchQuery.toLowerCase());
    const quizMatch = folder.quizzes?.some((quiz) =>
      quiz.title.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (searchFilter === "folders") {
      return folderNameMatch;
    } else if (searchFilter === "quizzes") {
      return quizMatch;
    } else {
      return folderNameMatch || quizMatch;
    }
  });

  const getPlaceholderText = () => {
    switch (searchFilter) {
      case "folders":
        return "Search courses...";
      case "quizzes":
        return "Search quizzes...";
      default:
        return "Search courses or quizzes...";
    }
  };

  const getFilterLabel = () => {
    switch (searchFilter) {
      case "folders":
        return "Courses Only";
      case "quizzes":
        return "Quizzes Only";
      default:
        return "All";
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        maxWidth: 1200,
        mx: "auto",
        minHeight: "100vh",
        background: theme.palette.background.default,
        borderRadius: "24px",
      }}
    >
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3, justifyContent: 'space-between' }}>
          <Typography
            variant="h3"
            gutterBottom
            sx={{
              fontWeight: "800",
              background: theme.palette.mode === 'light'
                ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              m: 0,
            }}
          >
            MY QUIZZES
          </Typography>
          <Tooltip title="User Guide" arrow>
            <IconButton
              onClick={() => setInfoModalOpen(true)}
              sx={{
                bgcolor: "rgba(102, 126, 234, 0.1)",
                "&:hover": { bgcolor: "rgba(102, 126, 234, 0.2)" },
                p: 1.5,
              }}
            >
              <InfoOutlinedIcon sx={{ color: "#667eea" }} />
            </IconButton>
          </Tooltip>
        </Box>

        <TextField
          fullWidth
          placeholder={getPlaceholderText()}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#667eea" }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  {searchQuery && (
                    <IconButton
                      size="small"
                      onClick={() => setSearchQuery("")}
                      sx={{
                        color: "#999",
                        "&:hover": {
                          color: "#667eea",
                          bgcolor: "rgba(102, 126, 234, 0.08)",
                        },
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                  <Tooltip title="Filter search" arrow>
                    <IconButton
                      onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                      sx={{
                        color: searchFilter !== "all" ? "#667eea" : "#999",
                        bgcolor: searchFilter !== "all" ? "rgba(102, 126, 234, 0.1)" : "transparent",
                        "&:hover": {
                          color: "#667eea",
                          bgcolor: "rgba(102, 126, 234, 0.15)",
                        },
                      }}
                    >
                      <FilterListIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "16px",
              bgcolor: theme.palette.background.paper,
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
              border: "2px solid transparent",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "& fieldset": {
                border: "none",
              },
              "&:hover": {
                boxShadow: "0 6px 24px rgba(102, 126, 234, 0.15)",
                transform: "translateY(-2px)",
                border: "2px solid rgba(102, 126, 234, 0.2)",
              },
              "&.Mui-focused": {
                boxShadow: "0 8px 32px rgba(102, 126, 234, 0.25)",
                transform: "translateY(-2px)",
                border: "2px solid #667eea",
                bgcolor: theme.palette.mode === 'light' ? "#fafbff" : "rgba(30, 41, 59, 0.5)",
              },
            },
            "& .MuiInputBase-input": {
              fontSize: "1rem",
              padding: "16px 14px",
              color: theme.palette.text.primary,
              "&::placeholder": {
                color: theme.palette.text.secondary,
                opacity: 1,
              },
            },
          }}
        />

        {searchFilter !== "all" && (
          <Chip
            label={`Filter: ${getFilterLabel()}`}
            onDelete={() => setSearchFilter("all")}
            icon={<span style={{ fontSize: "16px" }}>
              {searchFilter === "folders" ? "📁" : "📝"}
            </span>}
            sx={{
              mt: 1.5,
              bgcolor: "#667eea",
              color: "white",
              fontWeight: "600",
              borderRadius: "20px",
              "& .MuiChip-deleteIcon": {
                color: "white",
                "&:hover": {
                  color: "#ffeb3b",
                },
              },
            }}
          />
        )}
      </Box>

      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={() => setFilterAnchorEl(null)}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            mt: 1,
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
            minWidth: 200,
          }
        }}
      >
        <MenuItem
          onClick={() => {
            setSearchFilter("all");
            setFilterAnchorEl(null);
          }}
          selected={searchFilter === "all"}
          sx={{
            py: 1.5,
            px: 2,
            "&.Mui-selected": {
              bgcolor: "rgba(102, 126, 234, 0.1)",
              "&:hover": { bgcolor: "rgba(102, 126, 234, 0.15)" }
            }
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
            <span style={{ fontSize: "20px" }}>🔍</span>
            <Typography sx={{ flex: 1, fontWeight: searchFilter === "all" ? "600" : "400" }}>
              Search All
            </Typography>
            {searchFilter === "all" && <CheckIcon sx={{ color: "#667eea", fontSize: 20 }} />}
          </Box>
        </MenuItem>

        <MenuItem
          onClick={() => {
            setSearchFilter("folders");
            setFilterAnchorEl(null);
          }}
          selected={searchFilter === "folders"}
          sx={{
            py: 1.5,
            px: 2,
            "&.Mui-selected": {
              bgcolor: "rgba(102, 126, 234, 0.1)",
              "&:hover": { bgcolor: "rgba(102, 126, 234, 0.15)" }
            }
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
            <FolderIcon sx={{ color: "#667eea", fontSize: 20 }} />
            <Typography sx={{ flex: 1, fontWeight: searchFilter === "folders" ? "600" : "400" }}>
              Courses Only
            </Typography>
            {searchFilter === "folders" && <CheckIcon sx={{ color: "#667eea", fontSize: 20 }} />}
          </Box>
        </MenuItem>

        <MenuItem
          onClick={() => {
            setSearchFilter("quizzes");
            setFilterAnchorEl(null);
          }}
          selected={searchFilter === "quizzes"}
          sx={{
            py: 1.5,
            px: 2,
            "&.Mui-selected": {
              bgcolor: "rgba(102, 126, 234, 0.1)",
              "&:hover": { bgcolor: "rgba(102, 126, 234, 0.15)" }
            }
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
            <AssignmentIcon sx={{ color: "#667eea", fontSize: 20 }} />
            <Typography sx={{ flex: 1, fontWeight: searchFilter === "quizzes" ? "600" : "400" }}>
              Quizzes Only
            </Typography>
            {searchFilter === "quizzes" && <CheckIcon sx={{ color: "#667eea", fontSize: 20 }} />}
          </Box>
        </MenuItem>
      </Menu>

      {filteredFolders.length === 0 ? (
        <Paper
          sx={{
            p: 3,
            textAlign: "center",
            bgcolor: theme.palette.mode === 'light' ? "#ffe7e7" : "rgba(173, 23, 23, 0.2)",
            color: theme.palette.mode === 'light' ? "#a00" : "#ffcccc",
            borderRadius: "16px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: "600" }}>
            {searchQuery
              ? `No ${searchFilter === "all" ? "folders or quizzes" : searchFilter} found matching "${searchQuery}"`
              : "You have no assigned courses yet. Please contact your teacher."}
          </Typography>
        </Paper>
      ) : (
        filteredFolders.map((folder) => (
          <Accordion
            key={folder.id}
            defaultExpanded={false}
            sx={{
              mb: 3,
              borderRadius: "16px !important",
              boxShadow: "0 8px 30px rgba(0, 0, 0, 0.1)",
              border: `1px solid ${theme.palette.divider}`,
              "&:before": { display: "none" },
              transition: "all 0.3s ease",
              "&:hover": {
                boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: "#667eea" }} />}
              sx={{
                bgcolor: theme.palette.mode === 'light' ? "#f8f9ff" : "rgba(55, 65, 81, 0.5)",
                borderRadius: "16px",
                minHeight: "64px",
                "&.Mui-expanded": {
                  minHeight: "64px",
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <span style={{ fontSize: "24px" }}>📁</span>
                <Typography
                  variant="h5"
                  sx={{ color: theme.palette.text.primary, fontWeight: "700" }}
                >
                  {folder.name.toUpperCase()}
                </Typography>
                <Chip
                  label={`${folder.quizzes?.length || 0} quiz${folder.quizzes?.length !== 1 ? 'zes' : ''}`}
                  size="small"
                  sx={{
                    ml: 2,
                    backgroundColor: theme.palette.mode === 'light' ? "#e3f2fd" : "rgba(25, 118, 210, 0.2)",
                    color: "#1976d2",
                    fontWeight: "500",
                  }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              {folder.quizzes && folder.quizzes.length > 0 ? (
                folder.quizzes
                  .filter((quiz) => {
                    if (!searchQuery) return true;

                    const folderNameMatches = folder.name.toLowerCase().includes(searchQuery.toLowerCase());
                    const quizTitleMatches = quiz.title.toLowerCase().includes(searchQuery.toLowerCase());

                    if (searchFilter === "folders" && folderNameMatches) {
                      return true;
                    }

                    if (searchFilter === "quizzes") {
                      return quizTitleMatches;
                    }

                    return folderNameMatches || quizTitleMatches;
                  })
                  .map((quiz) => (
                    <Paper
                      key={quiz.id}
                      sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: "12px",
                        border: `1px solid ${theme.palette.divider}`,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: "10px" }}
                      >
                        <Typography sx={{ color: "#e8710a", fontSize: "18px" }}>
                          ↳
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: "500", color: theme.palette.text.primary }}>
                          {quiz.title}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          gap: "20px",
                          fontSize: "14px",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            gap: "16px",
                            alignItems: "center",
                          }}
                        >
                          <Chip
                            label={`${quiz.total_questions} Qs`}
                            size="small"
                            sx={{
                              backgroundColor: theme.palette.mode === 'light' ? "#e3f2fd" : "rgba(25, 118, 210, 0.2)",
                              color: "#1976d2",
                              fontWeight: "500",
                              borderRadius: "8px",
                            }}
                          />
                          <Chip
                            label={`${quiz.time_limit_minutes || "∞"} min`}
                            size="small"
                            sx={{
                              backgroundColor: theme.palette.mode === 'light' ? "#e8f5e9" : "rgba(46, 125, 50, 0.2)",
                              color: "#2e7d32",
                              fontWeight: "500",
                              borderRadius: "8px",
                            }}
                          />
                          <Chip
                            label={
                              quiz.remaining_attempts !== null
                                ? `${quiz.remaining_attempts} attempt${quiz.remaining_attempts !== 1 ? "s" : ""}`
                                : "∞ attempts"
                            }
                            size="small"
                            sx={{
                              backgroundColor: theme.palette.mode === 'light' ? "#f3e5f5" : "rgba(123, 31, 162, 0.2)",
                              color: "#7b1fa2",
                              fontWeight: "500",
                              borderRadius: "8px",
                            }}
                          />
                        </Box>
                        <Tooltip
                          title={
                            quiz.remaining_attempts !== null &&
                              quiz.remaining_attempts <= 0
                              ? "You don't have any more attempts left for the quiz"
                              : ""
                          }
                          arrow
                          placement="top"
                        >
                          <span>
                            <Button
                              variant="contained"
                              onClick={() => startQuiz(quiz.id)}
                              disabled={
                                quiz.remaining_attempts !== null &&
                                quiz.remaining_attempts <= 0
                              }
                              sx={{
                                borderRadius: "20px",
                                padding: "6px 16px",
                                fontSize: "13px",
                                fontWeight: "600",
                                textTransform: "none",
                                background:
                                  quiz.remaining_attempts !== null &&
                                    quiz.remaining_attempts <= 0
                                    ? theme.palette.mode === 'light' ? "#d1d5db" : "rgba(209, 213, 219, 0.5)"
                                    : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                color:
                                  quiz.remaining_attempts !== null &&
                                    quiz.remaining_attempts <= 0
                                    ? theme.palette.text.secondary
                                    : "#fff",
                                "&:hover": {
                                  background:
                                    quiz.remaining_attempts !== null &&
                                      quiz.remaining_attempts <= 0
                                      ? theme.palette.mode === 'light' ? "#d1d5db" : "rgba(209, 213, 219, 0.5)"
                                      : "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                                },
                              }}
                            >
                              Start Quiz
                            </Button>
                          </span>
                        </Tooltip>
                      </Box>
                    </Paper>
                  ))
              ) : (
                <Typography sx={{ p: 2, color: theme.palette.text.secondary, textAlign: "center" }}>
                  No quizzes available in this course.
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        ))
      )}

      {/* Quiz History Modal */}
      <Modal open={historyModalOpen} onClose={() => setHistoryModalOpen(false)}>
        <Box
          sx={{
            p: 0,
            bgcolor: theme.palette.background.paper,
            mx: "auto",
            mt: "3%",
            mb: "3%",
            width: "90%",
            maxWidth: 900,
            maxHeight: "90vh",
            borderRadius: "20px",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.2)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              p: 3,
              bgcolor: theme.palette.mode === 'light' ? "#1976d2" : "rgba(25, 118, 210, 0.8)",
              color: "white",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "4px solid #1565c0",
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                Quiz History
              </Typography>
              {quizHistory.length > 0 && (
                <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                  {quizHistory.reduce(
                    (total, quiz) => total + quiz.attempts.length,
                    0
                  )}{" "}
                  Attempts
                </Typography>
              )}
            </Box>
            <IconButton
              onClick={() => setHistoryModalOpen(false)}
              sx={{
                color: "white",
                "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ p: 3, overflowY: "auto", flex: 1 }}>
            {quizHistory.length > 0 ? (
              quizHistory.map((quiz, quizIdx) => (
                <Box key={quizIdx} sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: "bold",
                      mb: 1,
                      color: "#667eea",
                      borderBottom: "2px solid #e0e7ff",
                      pb: 1,
                    }}
                  >
                    {quiz.title}
                  </Typography>
                  {quiz.attempts.length > 0 ? (
                    quiz.attempts.map((attempt, attemptIdx) => (
                      <Paper
                        key={attemptIdx}
                        elevation={2}
                        sx={{
                          p: 3,
                          mb: 2,
                          border:
                            attempt.result === "Pass"
                              ? "2px solid #4caf50"
                              : "2px solid #f44336",
                          borderRadius: "16px",
                          bgcolor:
                            attempt.result === "Pass" ? "#f1f8f4" : "#fef5f5",
                          transition: "all 0.3s ease",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <Chip
                            label={`Attempt #${attempt.attempt_number}`}
                            size="small"
                            sx={{
                              bgcolor: "#1976d2",
                              color: "white",
                              fontWeight: "bold",
                              borderRadius: "8px",
                            }}
                          />
                          <Chip
                            label={attempt.result}
                            size="small"
                            sx={{
                              bgcolor:
                                attempt.result === "Pass"
                                  ? "#4caf50"
                                  : "#f44336",
                              color: "white",
                              fontWeight: "bold",
                              borderRadius: "8px",
                            }}
                          />
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Score:</strong> {attempt.final_score}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Status:</strong> {attempt.quiz_status}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ mb: 1, color: theme.palette.text.secondary }}
                          >
                            <strong>Started:</strong>{" "}
                            {new Date(attempt.started_at).toLocaleString()}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ mb: 1, color: theme.palette.text.secondary }}
                          >
                            <strong>Ended:</strong>{" "}
                            {new Date(attempt.ended_at).toLocaleString()}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            mt: 2,
                          }}
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              setHistoryModalOpen(false);
                              fetchReviewQA(
                                quiz.quiz_id,
                                attempt.quiz_attempt_id
                              );
                            }}
                            sx={{
                              borderRadius: "12px",
                              textTransform: "none",
                              fontWeight: "600",
                              borderColor: "#667eea",
                              color: "#667eea",
                              "&:hover": {
                                borderColor: "#667eea",
                                background: "rgba(102, 126, 234, 0.08)",
                              },
                            }}
                          >
                            Review QA
                          </Button>
                        </Box>
                      </Paper>
                    ))
                  ) : (
                    <Typography
                      sx={{
                        textAlign: "center",
                        py: 2,
                        color: theme.palette.text.secondary,
                      }}
                    >
                      No attempts yet.
                    </Typography>
                  )}
                </Box>
              ))
            ) : (
              <Box sx={{ textAlign: "center", py: 5 }}>
                <Typography variant="h6" color={theme.palette.text.secondary}>
                  No history available
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              p: 2,
              bgcolor: theme.palette.mode === 'light' ? "#f5f5f5" : "rgba(55, 65, 81, 0.5)",
              borderTop: `1px solid ${theme.palette.divider}`,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="contained"
              onClick={() => setHistoryModalOpen(false)}
              sx={{
                minWidth: 120,
                borderRadius: "12px",
                padding: "10px 24px",
                fontWeight: "600",
                textTransform: "none",
                background: theme.palette.mode === 'light'
                  ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                  : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
                "&:hover": {
                  background: theme.palette.mode === 'light'
                    ? "linear-gradient(135deg, #764ba2 0%, #667eea 100%)"
                    : "linear-gradient(135deg, #9333ea 0%, #818cf8 100%)",
                },
              }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Enhanced Review Modal */}
      <Modal open={reviewModalOpen} onClose={() => setReviewModalOpen(false)}>
        <Box
          sx={{
            p: 0,
            bgcolor: theme.palette.background.paper,
            mx: "auto",
            mt: "3%",
            mb: "3%",
            width: "90%",
            maxWidth: 900,
            maxHeight: "90vh",
            borderRadius: "20px",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.2)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              p: 3,
              bgcolor: theme.palette.mode === 'light' ? "#1976d2" : "rgba(25, 118, 210, 0.8)",
              color: "white",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "4px solid #1565c0",
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                Quiz Review
              </Typography>
              {reviewData && (
                <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                  {reviewData.review?.length || 0} Questions
                </Typography>
              )}
            </Box>
            <IconButton
              onClick={() => setReviewModalOpen(false)}
              sx={{
                color: "white",
                "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ p: 3, overflowY: "auto", flex: 1 }}>
            {reviewData?.review?.length > 0 ? (
              reviewData.review.map((q, idx) => {
                const wasAttempted =
                  q.given_answer && q.given_answer !== "Not Attempted";
                return (
                  <Paper
                    key={idx}
                    elevation={2}
                    sx={{
                      p: 3,
                      mb: 3,
                      border: q.is_correct
                        ? "2px solid #4caf50"
                        : wasAttempted
                          ? "2px solid #f44336"
                          : "2px solid #9e9e9e",
                      borderRadius: "16px",
                      bgcolor: q.is_correct
                        ? "#f1f8f4"
                        : wasAttempted
                          ? "#fef5f5"
                          : "#fafafa",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 2,
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Chip
                            label={`Question ${idx + 1}`}
                            size="small"
                            sx={{
                              bgcolor: "#1976d2",
                              color: "white",
                              fontWeight: "bold",
                              borderRadius: "8px",
                            }}
                          />
                          <Chip
                            label={q.question_type}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: "#1976d2",
                              color: "#1976d2",
                              borderRadius: "8px",
                            }}
                          />
                        </Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: "600",
                            color: theme.palette.text.primary,
                            mt: 1,
                            lineHeight: 1.6,
                          }}
                        >
                          {q.statement}
                        </Typography>
                      </Box>
                      <Box>
                        {q.is_correct ? (
                          <CheckCircleIcon
                            sx={{ fontSize: 40, color: "#4caf50" }}
                          />
                        ) : wasAttempted ? (
                          <CancelIcon sx={{ fontSize: 40, color: "#f44336" }} />
                        ) : (
                          <InfoOutlinedIcon
                            sx={{ fontSize: 40, color: "#9e9e9e" }}
                          />
                        )}
                      </Box>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ mt: 2 }}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          mb: 2,
                          bgcolor: wasAttempted
                            ? q.is_correct
                              ? "#e8f5e9"
                              : "#ffebee"
                            : "#f5f5f5",
                          border: wasAttempted
                            ? q.is_correct
                              ? "1px solid #4caf50"
                              : "1px solid #f44336"
                            : "1px solid #e0e0e0",
                          borderRadius: "12px",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: "bold",
                              color: wasAttempted
                                ? q.is_correct
                                  ? "#2e7d32"
                                  : "#c62828"
                                : theme.palette.text.secondary,
                            }}
                          >
                            Your Answer:
                          </Typography>
                          {!wasAttempted && (
                            <Chip
                              label="Not Attempted"
                              size="small"
                              sx={{
                                bgcolor: "#9e9e9e",
                                color: "white",
                                fontSize: "0.75rem",
                                borderRadius: "8px",
                              }}
                            />
                          )}
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "1rem",
                            color: wasAttempted
                              ? q.is_correct
                                ? "#2e7d32"
                                : "#c62828"
                              : theme.palette.text.secondary,
                            fontWeight: wasAttempted ? "500" : "normal",
                            fontStyle: !wasAttempted ? "italic" : "normal",
                          }}
                        >
                          {Array.isArray(q.given_answer)
                            ? q.given_answer.join(", ")
                            : q.given_answer || "Not Attempted"}
                        </Typography>
                      </Paper>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          bgcolor: "#e8f5e9",
                          border: "1px solid #4caf50",
                          borderRadius: "12px",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: "bold",
                            color: "#2e7d32",
                            mb: 1,
                          }}
                        >
                          Correct Answer:
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "1rem",
                            color: "#2e7d32",
                            fontWeight: "500",
                          }}
                        >
                          {Array.isArray(q.correct_answer)
                            ? q.correct_answer.join(", ")
                            : q.correct_answer}
                        </Typography>
                      </Paper>
                      {q.explanation && (
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            mt: 2,
                            bgcolor: "#e3f2fd",
                            border: "1px solid #2196f3",
                            borderRadius: "12px",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: "bold",
                              color: "#1565c0",
                              mb: 1,
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                            Explanation:
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.95rem",
                              color: "#1565c0",
                              lineHeight: 1.6,
                            }}
                          >
                            {q.explanation}
                          </Typography>
                        </Paper>
                      )}
                    </Box>
                  </Paper>
                );
              })
            ) : (
              <Box sx={{ textAlign: "center", py: 5 }}>
                <Typography variant="h6" color={theme.palette.text.secondary}>
                  No review data available
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              p: 2,
              bgcolor: theme.palette.mode === 'light' ? "#f5f5f5" : "rgba(55, 65, 81, 0.5)",
              borderTop: `1px solid ${theme.palette.divider}`,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="contained"
              onClick={() => setReviewModalOpen(false)}
              sx={{
                minWidth: 120,
                borderRadius: "12px",
                padding: "10px 24px",
                fontWeight: "600",
                textTransform: "none",
                background: theme.palette.mode === 'light'
                  ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                  : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
                "&:hover": {
                  background: theme.palette.mode === 'light'
                    ? "linear-gradient(135deg, #764ba2 0%, #667eea 100%)"
                    : "linear-gradient(135deg, #9333ea 0%, #818cf8 100%)",
                },
              }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Modal open={infoModalOpen} onClose={() => setInfoModalOpen(false)}>
        <Box
          sx={{
            p: 0,
            bgcolor: theme.palette.background.paper,
            mx: "auto",
            mt: "3%",
            mb: "3%",
            width: "90%",
            maxWidth: 800,
            maxHeight: "90vh",
            borderRadius: "16px",
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",

   "&::-webkit-scrollbar": {
      width: "10px",
      height: "10px",
    },
    "&::-webkit-scrollbar-track": {
      background: theme.palette.mode === 'dark' ? '#2d3748' : '#f1f5f9',
      borderRadius: "6px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: theme.palette.mode === 'dark' ? '#475569' : '#94a3b8',
      borderRadius: "6px",
      border: `2px solid ${theme.palette.mode === 'dark' ? '#2d3748' : '#f1f5f9'}`,
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: theme.palette.mode === 'dark' ? '#64748b' : '#64748b',
    },
    // Custom scrollbar for Firefox
    scrollbarWidth: "thin",
    scrollbarColor: theme.palette.mode === 'dark' ? '#475569 #2d3748' : '#94a3b8 #f1f5f9',
  }}
            
         
        >
          <Box
            sx={{
              p: 3.5,
              background: theme.palette.mode === 'light'
                ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
              color: "white",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: "700", mb: 0.5 }}>
                User Guide
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                My Quizzes
              </Typography>
            </Box>
          </Box>

          <Box sx={{ p: 4, overflowY: "auto", flex: 1 }}>
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    background: theme.palette.mode === 'light'
                      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                      : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 2,
                  }}
                >
                  <SearchIcon sx={{ color: "white", fontSize: 20 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: "600", color: theme.palette.text.primary }}>
                  Search & Filter
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7, pl: 7 }}>
                Use the search bar to find courses or quizzes by name. Click the filter icon to choose between searching all content, courses only, or quizzes only. Results filter automatically as you type.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    background: theme.palette.mode === 'light'
                      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                      : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 2,
                  }}
                >
                  <FolderIcon sx={{ color: "white", fontSize: 20 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: "600", color: theme.palette.text.primary }}>
                  Courses
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7, pl: 7 }}>
                Courses organize your assigned quizzes. Click any Course to expand and view its contents.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    background: theme.palette.mode === 'light'
                      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                      : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 2,
                  }}
                >
                  <AssignmentIcon sx={{ color: "white", fontSize: 20 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: "600", color: theme.palette.text.primary }}>
                  Quiz Information
                </Typography>
              </Box>
              <Box sx={{ pl: 7 }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7, mb: 2 }}>
                  Each quiz displays the following details:
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "start" }}>
                    <Box
                      sx={{
                        minWidth: 6,
                        height: 6,
                        borderRadius: "50%",
                        bgcolor: "#667eea",
                        mt: 0.8,
                        mr: 1.5,
                      }}
                    />
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                      <strong>Total Questions:</strong> Number of questions in the quiz
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "start" }}>
                    <Box
                      sx={{
                        minWidth: 6,
                        height: 6,
                        borderRadius: "50%",
                        bgcolor: "#667eea",
                        mt: 0.8,
                        mr: 1.5,
                      }}
                    />
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                      <strong>Time Limit:</strong> Quiz duration (if applicable)
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "start" }}>
                    <Box
                      sx={{
                        minWidth: 6,
                        height: 6,
                        borderRadius: "50%",
                        bgcolor: "#667eea",
                        mt: 0.8,
                        mr: 1.5,
                      }}
                    />
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                      <strong>Remaining Attempts:</strong> Available quiz attempts
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    background: theme.palette.mode === 'light'
                      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                      : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 2,
                  }}
                >
                  <PlayArrowIcon sx={{ color: "white", fontSize: 20 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: "600", color: theme.palette.text.primary }}>
                  Starting a Quiz
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7, pl: 7 }}>
                Click the "Start Quiz" button to begin. If attempts are exhausted, the button will be disabled.
              </Typography>
            </Box>

            <Box
              sx={{
                p: 3,
                borderRadius: "12px",
                bgcolor: theme.palette.mode === 'light' ? "#f7fafc" : "rgba(55, 65, 81, 0.5)",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: "600", color: theme.palette.text.primary, mb: 2 }}>
                💡 Quick Tips
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                  • Use search filters to narrow down results by Courses or quizzes
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                  • Empty courses display "No quizzes available"
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                  • Contact your teacher if no courses are assigned
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                  • Active filters show as a colored chip below the search bar
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              p: 3,
              bgcolor: theme.palette.mode === 'light' ? "#f7fafc" : "rgba(55, 65, 81, 0.5)",
              borderTop: `1px solid ${theme.palette.divider}`,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="contained"
              onClick={() => setInfoModalOpen(false)}
              sx={{
                minWidth: 120,
                borderRadius: "10px",
                padding: "10px 28px",
                fontWeight: "600",
                textTransform: "none",
                background: theme.palette.mode === 'light'
                  ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                  : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
                boxShadow: theme.palette.mode === 'light'
                  ? "0 4px 12px rgba(102, 126, 234, 0.3)"
                  : "0 4px 12px rgba(130, 142, 248, 0.3)",
                "&:hover": {
                  background: theme.palette.mode === 'light'
                    ? "linear-gradient(135deg, #764ba2 0%, #667eea 100%)"
                    : "linear-gradient(135deg, #9333ea 0%, #818cf8 100%)",
                  boxShadow: theme.palette.mode === 'light'
                    ? "0 6px 16px rgba(102, 126, 234, 0.4)"
                    : "0 6px 16px rgba(130, 142, 248, 0.4)",
                },
              }}
            >
              Got it
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default StudentQuizzes;